// Walkthrough item 4: a blocked blog draft must explain itself. The blog quality gate's
// findings are recorded on the Run's output (`quality.semantic_findings` /
// `quality.deterministic_findings`, written by blog-compose-for-review); this projects them
// into an operator-readable view for the brief page and the Run page. Pure and defensive:
// the output is untrusted JSON, so anything malformed is skipped, never thrown.

export type BlogGateFindingView = {
  dimension: string;
  dimension_label: string;
  code: string;
  code_label: string;
  passage: string;
  instruction: string;
};

export type BlogGateFailureView = {
  code: string;
  headline: string;
  findings: BlogGateFindingView[];
  /** Mechanical checks (word range, heading, required link…), as plain sentences. */
  checks: string[];
  /** Set when the model's answer could not be read at all (not a content verdict). */
  parse_failure: { stage: string; reason: string } | null;
};

const MAX_FINDINGS = 20;
const MAX_TEXT = 500;

const DIMENSION_LABELS: Record<string, string> = {
  AUDIENCE: "Audience", VALUE: "Value to the reader", EVIDENCE: "Evidence", SOURCE_BOUNDARY: "Stays within sources",
  SEARCH_INTENT: "Search intent", CLARITY: "Clarity", VOICE: "Brand voice", HUMAN_READABILITY: "Readability",
  FEEDBACK_FULFILLMENT: "Your revision feedback",
};

const CHECK_LABELS: Record<string, string> = {
  WORD_RANGE: "The draft is outside the brief's word range.",
  HEADING_MISSING: "The draft has no heading.",
  REQUIRED_LINK_MISSING: "The draft is missing the brief's required link.",
  SECRET_LEAK: "The draft contains something that looks like a secret key.",
  PLACEHOLDER: "The draft still contains a placeholder such as [TODO].",
};

const HEADLINES: Record<string, string> = {
  QUALITY_GATE_FAILED: "The draft did not pass the quality review, even after one automatic revision.",
  blog_model_output_truncated: "The writing model ran out of output budget before finishing. Try again.",
};

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
const text = (value: unknown, max = MAX_TEXT): string =>
  typeof value === "string" ? value.trim().replace(/\s+/g, " ").slice(0, max).trim() : "";

/** "UNSUPPORTED_RESEARCH_CLAIM" → "Unsupported research claim". */
export function humanizeGateCode(code: string): string {
  const words = code.replace(/[^A-Za-z0-9]+/g, " ").trim().toLowerCase();
  return words ? words[0]!.toUpperCase() + words.slice(1) : code;
}

export function gateDimensionLabel(dimension: string): string {
  return DIMENSION_LABELS[dimension] ?? humanizeGateCode(dimension);
}

const BLOCKED_SUMMARY = /^Blog (?:compose|revision) blocked: ([A-Za-z0-9_]+)\s*$/;

/** Null unless the output records a blocked blog compose or revision. */
export function blogGateFailureFromRunOutput(output: unknown): BlogGateFailureView | null {
  const root = record(output);
  if (!root) return null;
  const metadata = record(root.metadata);
  const quality = record(root.quality) ?? record(metadata?.quality);
  const summary = text(root.summary) || text(metadata?.summary);
  const blocked = BLOCKED_SUMMARY.exec(summary);
  const status = text(root.compose_status) || text(root.revision_status) || text(metadata?.compose_status) || text(metadata?.revision_status);
  if (!blocked && !(quality && status && status !== "passed")) return null;
  const code = blocked?.[1] ?? (status || "QUALITY_GATE_FAILED");

  const findings: BlogGateFindingView[] = [];
  for (const raw of Array.isArray(quality?.semantic_findings) ? quality!.semantic_findings : []) {
    const row = record(raw);
    const dimension = text(row?.dimension, 64);
    const findingCode = text(row?.code, 64);
    if (!row || !dimension) continue;
    findings.push({
      dimension, dimension_label: gateDimensionLabel(dimension),
      code: findingCode, code_label: findingCode ? humanizeGateCode(findingCode) : "",
      passage: text(row.passage), instruction: text(row.instruction),
    });
    if (findings.length >= MAX_FINDINGS) break;
  }
  const checks = [...new Set((Array.isArray(quality?.deterministic_findings) ? quality!.deterministic_findings : [])
    .filter((value): value is string => typeof value === "string" && !!value.trim())
    .map(value => CHECK_LABELS[value] ?? `${humanizeGateCode(value)}.`))].slice(0, MAX_FINDINGS);
  const parse = record(quality?.parse_failure);
  return {
    code,
    headline: HEADLINES[code] ?? `The draft was blocked (${humanizeGateCode(code)}).`,
    findings,
    checks,
    parse_failure: parse && text(parse.reason, 64) ? { stage: text(parse.stage, 32), reason: text(parse.reason, 64) } : null,
  };
}
